import React, { useState } from "react";
import { Modal, Form, Select, Input, Alert, List, Typography, Space, Spin, Tag, Radio } from "antd";
import { EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { uldService } from "../../../../api/enhancedApi";
import { ULD_CORE_STATUS, ULD_CORE_STATUS_NAMES, ULD_CORE_STATUS_COLORS } from "../constants/uldConstantsV3";
import { ULD_LOCATION_TYPE } from "../constants/uldConstants";
import { getCommonTargetStatusOptions } from "../../lib/batchCoreStatus";
import { useUldAirports } from "../../hooks/useUldAirports";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * 批量更新ULD状态的弹窗组件
 */
const StatusBatchUpdateModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [targetStatus, setTargetStatus] = useState(null);
    const [recoveryLocationType, setRecoveryLocationType] = useState(ULD_LOCATION_TYPE.AIRPORT);
    const { airportsList, loading: airportsLoading } = useUldAirports();

    // 获取当前选中ULD的核心状态分布（V3）
    const getStatusDistribution = () => {
        const statusCounts = {};
        selectedUlds.forEach((uld) => {
            const coreStatus = uld.stock?.core_status || "IN_STOCK"; // 默认为IN_STOCK
            statusCounts[coreStatus] = (statusCounts[coreStatus] || 0) + 1;
        });
        return statusCounts;
    };

    const statusDistribution = getStatusDistribution();
    const targetStatusOptions = getCommonTargetStatusOptions(selectedUlds);

    // 勾选的全是丢失、且目标是在库/停用 → 批量找回：原因必填，并须确认这批板所在的同一个在库类地点
    const allSelectedLost =
        selectedUlds.length > 0 &&
        selectedUlds.every((uld) => (uld.stock?.core_status || "IN_STOCK") === ULD_CORE_STATUS.LOST);
    const isBatchRecover =
        allSelectedLost && [ULD_CORE_STATUS.IN_STOCK, ULD_CORE_STATUS.OUT_OF_SERVICE].includes(targetStatus);
    // 目标是丢失（标记丢失）：原因必填
    const isMarkLost = targetStatus === ULD_CORE_STATUS.LOST;
    const reasonRequired = isBatchRecover || isMarkLost;

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 提取ULD识别码
            const uldIdentifiers = selectedUlds.map((uld) => uld.master.uld_identifier);

            // 调用V3批量核心状态更新API
            const payload = {
                uld_identifiers: uldIdentifiers,
                new_core_status: values.core_status,
                remarks: values.remarks,
            };
            // 批量找回：同一次提交在库类地点（机场或不带趟次的其他地点）
            if (isBatchRecover) {
                payload.recovery_location_type = recoveryLocationType;
                if (recoveryLocationType === ULD_LOCATION_TYPE.AIRPORT) {
                    payload.recovery_airport_iata = values.recovery_airport_iata;
                } else {
                    payload.recovery_other_location = values.recovery_other_location;
                }
            }

            const response = await uldService.batchUpdateCoreStatus(payload);

                        // 按照API_GUIDELINES规范处理批量操作结果
            // API响应格式：{success: true, message: "...", data: {success_count, failure_count, ...}}
            const apiResult = response.data;
            const result = apiResult.data;
            
            if (result.success_count > 0) {
                if (result.failure_count > 0) {
                    // 部分成功的情况
                    Modal.info({
                        title: "批量状态更新完成",
                        content: (
                            <div>
                                <p>
                                    成功更新 <strong>{result.success_count}</strong> 个ULD，失败 <strong>{result.failure_count}</strong> 个
                                </p>
                                {result.failure_items && result.failure_items.length > 0 && (
                                    <div style={{ marginTop: 12 }}>
                                        <Text strong>失败详情：</Text>
                                        <List
                                            size="small"
                                            dataSource={result.failure_items}
                                            renderItem={(item) => (
                                                <List.Item>
                                                    <Text code>{String(item?.identifier || "未知")}</Text>: {String(item?.error || "未知错误")}
                                                </List.Item>
                                            )}
                                            style={{ marginTop: 8, maxHeight: 200, overflow: "auto" }}
                                        />
                                    </div>
                                )}
                            </div>
                        ),
                        width: 500,
                    });
                } else {
                    // 完全成功的情况
                    Modal.success({
                        title: "批量核心状态更新成功",
                        content: `成功更新 ${result.success_count} 个ULD的核心状态为 ${ULD_CORE_STATUS_NAMES[values.core_status]}`,
                    });
                }

                // 重置表单并关闭弹窗
                form.resetFields();
                setTargetStatus(null);
                setRecoveryLocationType(ULD_LOCATION_TYPE.AIRPORT);
                onSuccess && onSuccess();
            } else {
                // 完全失败的情况
                Modal.error({
                    title: "批量核心状态更新失败",
                    content: "所有ULD核心状态更新都失败了",
                });
            }
        } catch (error) {
            console.error("批量核心状态更新失败:", error);

            // 安全地处理错误信息
            let errorMessage = "请求失败";

            if (error.response?.data) {
                const errorData = error.response.data;
                if (typeof errorData === "string") {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = String(errorData.detail);
                } else if (errorData.message) {
                    errorMessage = String(errorData.message);
                } else {
                    try {
                        errorMessage = JSON.stringify(errorData);
                    } catch {
                        errorMessage = "解析错误信息失败";
                    }
                }
            } else if (error.message) {
                errorMessage = String(error.message);
            }

            Modal.error({
                title: "批量核心状态更新失败",
                content: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        setTargetStatus(null);
        setRecoveryLocationType(ULD_LOCATION_TYPE.AIRPORT);
        onCancel && onCancel();
    };

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    批量核心状态更新
                </Space>
            }
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="确认更新"
            cancelText="取消"
            confirmLoading={loading}
            okButtonProps={{ disabled: targetStatusOptions.length === 0 }}
            width={700}
            destroyOnHidden
        >
            <Form form={form} layout="vertical" preserve={false}>
                <Alert message={`将要更新 ${selectedUlds.length} 个ULD的核心状态信息`} type="info" showIcon style={{ marginBottom: 16 }} />

                {/* 当前核心状态分布（V3） */}
                {Object.keys(statusDistribution).length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>当前核心状态分布：</Text>
                        <div style={{ marginTop: 8 }}>
                            {Object.entries(statusDistribution).map(([coreStatus, count]) => (
                                <Tag key={coreStatus} color={ULD_CORE_STATUS_COLORS[coreStatus]} style={{ marginBottom: 4 }}>
                                    {ULD_CORE_STATUS_NAMES[coreStatus]}: {count}个
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}

                {targetStatusOptions.length === 0 ? (
                    <Alert
                        message="当前选中的ULD没有共同的合法下一状态"
                        description="请按同一核心状态勾选后再批量变更，或改为单条操作。"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                ) : (
                    <Form.Item name="core_status" label="新核心状态" rules={[{ required: true, message: "请选择新的核心状态" }]}>
                        <Select
                            placeholder="请选择新的核心状态"
                            options={targetStatusOptions}
                            showSearch
                            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                            onChange={(value) => setTargetStatus(value)}
                        />
                    </Form.Item>
                )}

                {isBatchRecover && (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            message="批量找回：请确认这批板现在所在的同一个在库类地点（机场，或不带运输趟次的其他地点）。若板还在飞机或卡车上，等到站后再找回，或走报文强制通过。"
                            style={{ marginBottom: 16 }}
                        />
                        <Form.Item label="找回位置（在库类地点）" required style={{ marginBottom: 8 }}>
                            <Radio.Group
                                value={recoveryLocationType}
                                onChange={(e) => setRecoveryLocationType(e.target.value)}
                            >
                                <Radio value={ULD_LOCATION_TYPE.AIRPORT}>机场</Radio>
                                <Radio value={ULD_LOCATION_TYPE.OTHER_LOCATION}>其他地点</Radio>
                            </Radio.Group>
                        </Form.Item>
                        {recoveryLocationType === ULD_LOCATION_TYPE.AIRPORT ? (
                            <Form.Item
                                name="recovery_airport_iata"
                                label="机场"
                                rules={[{ required: true, message: "请选择机场" }]}
                            >
                                <Select
                                    placeholder="请选择机场"
                                    showSearch
                                    loading={airportsLoading}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    notFoundContent={airportsLoading ? <Spin size="small" /> : "暂无数据"}
                                >
                                    {(airportsList || []).map((airport) => (
                                        <Option key={airport.iata_code} value={airport.iata_code}>
                                            {airport.iata_code}
                                            {airport.name_cn || airport.name_en
                                                ? ` - ${airport.name_cn || airport.name_en}`
                                                : ""}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        ) : (
                            <Form.Item
                                name="recovery_other_location"
                                label="其他地点描述"
                                rules={[{ required: true, message: "请填写位置描述" }]}
                                extra="不能是带运输趟次的地点（那属于在途）"
                            >
                                <Input placeholder="例如：维修车间" maxLength={255} />
                            </Form.Item>
                        )}
                    </>
                )}

                <Form.Item
                    name="remarks"
                    label={reasonRequired ? "原因（必填）" : "变更备注"}
                    rules={[
                        { max: 500, message: "备注不能超过500个字符" },
                        ...(reasonRequired ? [{ required: true, whitespace: true, message: "必须填写原因" }] : []),
                    ]}
                >
                    <TextArea
                        placeholder={
                            isMarkLost
                                ? "必须填写原因：例如航班到站清点缺少、仓库盘点未发现"
                                : isBatchRecover
                                  ? "必须填写原因：请说明在哪里找到了这批板、板况如何"
                                  : "请输入核心状态变更的原因或备注信息（可选）"
                        }
                        rows={3}
                        showCount
                        maxLength={500}
                    />
                </Form.Item>

                <div style={{ marginTop: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                        <Text strong>将要更新的ULD列表：</Text>
                        <InfoCircleOutlined style={{ marginLeft: 4, color: "#1890ff" }} title="显示当前选中的所有ULD及其当前状态" />
                    </div>
                    <List
                        size="small"
                        style={{
                            maxHeight: 200,
                            overflow: "auto",
                            border: "1px solid #d9d9d9",
                            borderRadius: 6,
                            padding: 8,
                        }}
                        dataSource={selectedUlds}
                        renderItem={(uld) => {
                            // 安全地获取数据，确保不会有对象被直接渲染
                            const identifier = String(uld?.master?.uld_identifier || "未知");
                            const coreStatus = uld?.stock?.core_status || "IN_STOCK";
                            const statusName = ULD_CORE_STATUS_NAMES[coreStatus] || String(coreStatus);
                            const statusColor = ULD_CORE_STATUS_COLORS[coreStatus] || "default";

                            return (
                                <List.Item style={{ padding: "4px 0" }}>
                                    <Space>
                                        <Text code>{identifier}</Text>
                                        <Text type="secondary">当前核心状态:</Text>
                                        <Tag color={statusColor} size="small">
                                            {statusName}
                                        </Tag>
                                    </Space>
                                </List.Item>
                            );
                        }}
                    />
                </div>

                {/* 状态转换说明 */}
                <Alert
                    message="状态更新说明"
                    description={
                        <div>
                            <Paragraph style={{ margin: 0, fontSize: "12px" }}>
                                • 状态更新会同时更新ULD的可用性标识
                                <br />
                                • 系统会自动记录状态变更历史
                                <br />
                                • 某些状态转换可能受到业务规则限制
                                <br />• 建议在重要状态变更时填写详细的备注信息
                            </Paragraph>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            </Form>

            {loading && (
                <div style={{ textAlign: "center", padding: 20 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary">正在更新ULD状态信息...</Text>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default StatusBatchUpdateModal;
